import axios, { type AxiosPromise } from "axios";
import { err, ok, ResultAsync, type Result } from "neverthrow";
import { RichErrors, type RichError } from "./errors";
import { makeAxiosRequest, UnhandledDiscriminatorError } from "./network";
import { sleep } from "./promises";

export interface PollConfig<Failure> {
  delay?: number[];
  retry?: {
    max?: number;
    delay?: number;
    predicate: (error: RichError<Failure>) => boolean;
  };
}

export const poll = <
  Response extends Record<DiscriminatorKey, string>,
  DiscriminatorKey extends keyof Response,
  SuccessValue extends string & Response[DiscriminatorKey],
  FailureValue extends string & Response[DiscriminatorKey],
  PendingValue extends string & Response[DiscriminatorKey],
  Success = Response & { [k in DiscriminatorKey]: SuccessValue },
  Failure = Response & { [k in DiscriminatorKey]: FailureValue },
>(
  performPoll: () => AxiosPromise<Response>,
  process: {
    discriminator: keyof Response;
    success: SuccessValue;
    failure: FailureValue;
    pending: PendingValue;
  },
  config?: PollConfig<Failure> & {
    success?: (
      response: Response & { [k in DiscriminatorKey]: SuccessValue }
    ) => Success;
    failure?: (
      response: Response & { [k in DiscriminatorKey]: FailureValue }
    ) => Failure;
    other?: (
      response: Response & {
        [k in DiscriminatorKey]: Exclude<
          Response[DiscriminatorKey],
          SuccessValue | FailureValue
        >;
      }
    ) => Result<Success, RichError<Failure>>;
  }
): ResultAsync<Success, RichError<Failure>> => {
  const mapSuccess = config?.success ?? ((response) => response as Success);
  const mapFailure = config?.failure ?? ((response) => response as Failure);
  const mapOther =
    config?.other ??
    ((response) =>
      err(
        RichErrors.unknown(
          new UnhandledDiscriminatorError(
            response[process.discriminator],
            response
          )
        )
      ));

  const maxRetries = config?.retry?.max ?? 5;
  const retryDelay = config?.retry?.delay ?? 1500;
  const delays = config?.delay ?? [2000, 4000, 6000, 8000, 10000, 20000, 30000];
  const shouldRetry =
    config?.retry?.predicate ??
    // By default, retry on any network errors or unknown errors
    ((err) => axios.isAxiosError(err) || err.type === "unknown");

  const doPoll = async () => {
    // Tracks the number of contiguous retriable failures
    let retries = 0;
    // Tracks the number of poll attempts made
    let attempt = 0;

    while (true) {
      // Make the poll call
      const poll = await makeAxiosRequest(performPoll, process, {
        success: (success): { success: Success } | "pending" => ({
          success: mapSuccess(success),
        }),
        failure: mapFailure,
        other: (response) => {
          if ((response[process.discriminator] as string) === process.pending) {
            return ok("pending" as const);
          }
          return mapOther(
            response as Response & {
              [k in DiscriminatorKey]: Exclude<
                Response[DiscriminatorKey],
                SuccessValue | FailureValue
              >;
            }
          ).map((success) => ({ success }));
        },
      });

      if (poll.isErr()) {
        // If the result is failure, check if we should retry
        if (retries < maxRetries && shouldRetry(poll.error)) {
          // If we should retry, first delay
          await sleep(retryDelay);
          // Then increment the number of retry attempts
          retries++;
          continue;
        }
        // If we're not retrying, propagate the failure
        return err(poll.error);
      }

      const { value } = poll;
      if (value !== "pending") {
        // When the result is a success, return it
        return ok(value.success);
      }

      // The poll result is pending. Since the primary use case is to avoid
      // showing an error to the user for temporary network or server issues,
      // we only care about contiguous retries.
      // If we get a pending result, we reset the retries counter
      retries = 0;

      // Delay the next poll attempt
      const delay = delays[Math.min(attempt, delays.length - 1)];
      await sleep(delay ?? 0);
      attempt++;
    }
  };

  return ResultAsync.fromSafePromise(doPoll()).andThen((r) => r);
};
